import Image from "next/image";

import { avatarToneClasses } from "@/components/site/sections/six-pillars-preview/charts-and-badges";
import { getSixPillarsPreviewPerson } from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

/** A Demo Team photo; "mine" rings it so the viewer's own items read at a glance. */
export function OwnerAvatar({
  name,
  size,
  mine = false,
  inverted = false,
}: {
  name: string;
  size: "sm" | "lg";
  mine?: boolean;
  inverted?: boolean;
}) {
  const person = getSixPillarsPreviewPerson(name);
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("");

  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex flex-none items-center justify-center overflow-hidden rounded-full font-semibold",
        size === "sm" ? "size-[22px] text-[9px]" : "size-11 text-[13px]",
        person ? avatarToneClasses[person.avatarTone] : "bg-muted text-foreground/75",
        mine &&
          (inverted
            ? "ring-[1.5px] ring-background ring-offset-[1.5px] ring-offset-foreground"
            : "ring-[1.5px] ring-foreground ring-offset-[1.5px] ring-offset-background"),
      )}
    >
      {person ? (
        <Image
          src={person.avatarSrc}
          alt=""
          fill
          sizes={size === "sm" ? "22px" : "44px"}
          className="object-cover"
        />
      ) : (
        initials
      )}
    </span>
  );
}

/** Owner as a small line on a card: photo and first name, or "You" for the viewer's items. */
export function OwnerBadge({
  name,
  mine,
  inverted = false,
}: {
  name: string;
  mine: boolean;
  inverted?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 text-xs font-medium",
        inverted ? "text-background/75" : "text-muted-foreground",
      )}
    >
      <OwnerAvatar name={name} size="sm" mine={mine} inverted={inverted} />
      <span className="sr-only">Suggested owner: </span>
      <span className="truncate">{mine ? "You" : name.split(/\s+/)[0]}</span>
      {mine ? <span className="sr-only"> ({name})</span> : null}
    </span>
  );
}
